import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Carrousel ANIMÉ des mascottes officielles KAIZEN (KAI · MEI · LANA).
 * - Auto-défilement (5s), pause au survol.
 * - Effet Ken Burns sur l'image + entrée du texte en fondu/glissé.
 * - Flèches + points de navigation.
 */
export default function MascotCarousel({ mascots = [], interval = 5000 }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = mascots.length;

  const go = useCallback((i) => setActive(((i % count) + count) % count), [count]);
  const next = useCallback(() => go(active + 1), [active, go]);
  const prev = useCallback(() => go(active - 1), [active, go]);

  useEffect(() => {
    if (paused || count <= 1) return;
    timer.current = setTimeout(() => setActive((a) => (a + 1) % count), interval);
    return () => clearTimeout(timer.current);
  }, [active, paused, count, interval]);

  if (!count) return null;

  return (
    <div
      data-testid="mascot-carousel"
      className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0b0b0b]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`
        @keyframes mc-kenburns { 0%{transform:scale(1.06)} 100%{transform:scale(1.0)} }
        @keyframes mc-fadeup { 0%{opacity:0;transform:translateY(18px)} 100%{opacity:1;transform:translateY(0)} }
        @keyframes mc-bar { 0%{width:0%} 100%{width:100%} }
      `}</style>

      <div className="relative h-[440px] sm:h-[420px]">
        {mascots.map((m, i) => {
          const on = i === active;
          return (
            <div
              key={m.name}
              data-testid={`mascot-slide-${m.name.toLowerCase()}`}
              aria-hidden={!on}
              className="absolute inset-0 transition-opacity duration-700 ease-out"
              style={{ opacity: on ? 1 : 0, pointerEvents: on ? "auto" : "none" }}
            >
              <div className="grid md:grid-cols-2 h-full">
                {/* Image */}
                <div className="relative h-72 md:h-full overflow-hidden bg-gradient-to-b from-[#15131c] to-[#0b0b0b]">
                  <img
                    src={`${BACKEND}/api/marketing-assets/file/${m.file}`}
                    alt={m.name}
                    className="w-full h-full object-contain object-center"
                    style={on ? { animation: "mc-kenburns 5s ease-out forwards", transformOrigin: "center center" } : { transformOrigin: "center center" }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: `linear-gradient(90deg, transparent 30%, #0b0b0b 98%), linear-gradient(0deg, ${m.accent}22, transparent 60%)` }}
                  />
                </div>
                {/* Texte */}
                <div className="relative flex flex-col justify-center p-7 sm:p-10">
                  {on && (
                    <div style={{ animation: "mc-fadeup .7s ease-out both" }}>
                      <div className="text-[11px] uppercase tracking-[0.35em] mb-3" style={{ color: m.accent }}>
                        Ambassadeur KAIZEN
                      </div>
                      <h3
                        className="font-black tracking-tight text-5xl sm:text-6xl leading-none mb-3 text-white"
                        style={{ fontFamily: "'Barlow Condensed', 'Cabinet Grotesk', sans-serif" }}
                      >
                        {m.name}
                      </h3>
                      <div className="text-lg font-bold mb-3" style={{ color: m.accent }}>{m.tagline}</div>
                      <p className="text-white/60 text-sm leading-relaxed max-w-md">{m.desc}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Flèches */}
        <button
          data-testid="mascot-carousel-prev"
          onClick={prev}
          aria-label="Mascotte précédente"
          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 border border-white/15 flex items-center justify-center text-white/80 hover:bg-black/80 hover:scale-110 transition-all"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          data-testid="mascot-carousel-next"
          onClick={next}
          aria-label="Mascotte suivante"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 border border-white/15 flex items-center justify-center text-white/80 hover:bg-black/80 hover:scale-110 transition-all"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Points + barre de progression */}
      <div className="flex items-center justify-center gap-3 py-4 bg-[#0b0b0b]">
        {mascots.map((m, i) => (
          <button
            key={m.name}
            data-testid={`mascot-carousel-dot-${i}`}
            onClick={() => go(i)}
            aria-label={`Aller à ${m.name}`}
            className="group relative h-2 rounded-full transition-all duration-300"
            style={{
              width: i === active ? 40 : 10,
              backgroundColor: i === active ? m.accent : "rgba(255,255,255,0.25)",
            }}
          >
            {i === active && !paused && (
              <span
                className="absolute inset-0 rounded-full opacity-40"
                style={{ background: "#fff", animation: `mc-bar ${interval}ms linear forwards` }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
